import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import cn from 'clsx';
import { Country, getData } from 'country-list';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { MultiSelectController } from '@components-clean/core/multi-select';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextBoxController } from '@components-clean/core/text-box';
import { NumericTextBoxController } from '@components-clean/core/text-box-numeric';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import styles from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import ctqStyles from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/style.module.scss';
import { CtqPageRiskFields } from '@pages-clean/risk-assessment/critical-to-quality-factors/types';
import { mapCountries, mapNamedItems } from '@pages-clean/risk-assessment/helpers';
import { getUTCDate } from '@utils-clean/date-time-helpers';
import { NamedItemType, RiskStatusType } from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useBoolean, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { javascriptDateWithoutTimeFromUTC, stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { RiskService } from '@utils/risk.service';
import { notEmpty } from '@utils/utility-helpers';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import {
    getNamedItemsByType,
    getNamedItemsByType_OpraQueries_namedItemsByType_NamedItem,
} from '@queries/generated/common.generated';
import { NamedItemFields } from '@queries/generated/fragments.generated';
import { getStudyMembers, getStudyMembersVariables } from '@queries/generated/studies.generated';
import { GET_STUDY_MEMBERS } from '@queries/studies';

interface AddRiskProps {
    studyRef: string;
    onClose: () => void;
    onSubmit: (data: RiskFormInputs) => void;
    risk?: CtqPageRiskFields;
}

type ExtendedNamedItem = NamedItemFields & {
    groupedBy: string;
    disabled: boolean;
};

export type RiskFormInputs = {
    reviewRequired: boolean | null;
    internal: boolean | null;
    status: { id: string; text: string } | null;
    reviewFrequencyUnit: { id: string; text: string } | null;
    reviewFrequencyPeriod: number | null;
    dateIdentified: Date | null;
    closedDate: Date | null;
    responsibleFunction: ExtendedNamedItem | null;
    owner: { fullName: string; id: string; groupedBy: string; active: boolean } | null;
    controlStrategy: ExtendedNamedItem | null;
    category: ExtendedNamedItem | null;
    vendors: ExtendedNamedItem[] | null;
    countries: Country[] | null;
    statement: string | null;
    qualityIssue: ExtendedNamedItem | null;
    qualityIssueReference: string | null;
};

const COMPONENT_NAME = 'add-risk';

const AddRiskDialog = ({ studyRef, onClose, onSubmit, risk }: AddRiskProps) => {
    const curUser = useAuthorization();

    const canViewInternal = curUser?.can?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.InternalRisks,
        permission: Permissions.Enabled,
    });

    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('ram', { keyPrefix: 'risks.dialogs.addRisk' });

    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const [responsibleFunctions, setResponsibleFunctions] = useState<
        ({ groupedBy: string; id: string; name: string; disabled: boolean; active: boolean | null } | null)[]
    >([]);

    const showApplyNotApplicableStatusDialog = useBoolean(false);
    const showApplyClosedStatusDialog = useBoolean(false);
    const showObsoleteStatusDialog = useBoolean(false);

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/purity
    const reviewFrequencyUnits: { id: string; text: string }[] = Object.assign([], RiskService.timeFrequencyListObject);

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/purity
    const statuses: { id: string; text: string }[] = Object.assign([], RiskService.riskStatusList);

    const { data: qualityIssueData, loading: loadingQualityIssueData } = useQuery<getNamedItemsByType>(
        GET_NAMED_ITEMS_BY_TYPE,
        {
            variables: { type: NamedItemType.RISK_QUALITY_ISSUE },
        },
    );

    const { data: responsibleFunctionData, loading: loadingResponsibleFunctionData } = useQuery<getNamedItemsByType>(
        GET_NAMED_ITEMS_BY_TYPE,
        {
            variables: { type: NamedItemType.RESPONSIBLE_FUNCTION },
        },
    );

    useEffect(() => {
        if (responsibleFunctionData?.namedItemsByType) {
            const studyFunctions = responsibleFunctionData?.namedItemsByType
                .filter(
                    (ni): ni is getNamedItemsByType_OpraQueries_namedItemsByType_NamedItem => ni?.studyId === studyRef,
                )
                .flatMap((studyFunction) => ({
                    groupedBy: studyFunction.active ? 'Active' : 'Inactive',
                    name: studyFunction.name ?? '',
                    id: (studyFunction.id as string) ?? '',
                    disabled: risk?.responsibleFunction
                        ? risk.responsibleFunction.name?.toLowerCase() === studyFunction?.name?.toLowerCase()
                            ? false
                            : !studyFunction.active
                        : !studyFunction.active,
                    active: studyFunction.active,
                    __typename: studyFunction.__typename,
                }))
                .sort((a, b) => {
                    if (a?.active !== b?.active) {
                        return a?.active ? -1 : 1;
                    }

                    if (a.name && b.name) {
                        return a.name?.localeCompare(b.name);
                    }

                    return a?.active ? -1 : 1;
                });
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setResponsibleFunctions(studyFunctions);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [responsibleFunctionData?.namedItemsByType]);

    const [owners, setOwners] = useState<
        ({ fullName: string; id: string; groupedBy: string; active: boolean } | null)[] | undefined
    >([]);

    const [loadOwnerData, { data: ownerData, loading: loadingOwners }] = useLazyQuery<
        getStudyMembers,
        getStudyMembersVariables
    >(GET_STUDY_MEMBERS);

    useEffect(() => {
        loadOwnerData({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    useEffect(() => {
        if (ownerData?.studyMembers) {
            const studyMembers = ownerData.studyMembers
                .filter((user) => user)
                .sort((aOwner, bOwner) => {
                    if (aOwner?.active !== bOwner?.active) {
                        return aOwner?.active ? -1 : 1;
                    }
                    const aName = `${aOwner?.firstName?.toLowerCase() ?? ''} ${aOwner?.lastName?.toLowerCase() ?? ''}`;
                    const bName = `${bOwner?.firstName?.toLowerCase() ?? ''} ${bOwner?.lastName?.toLowerCase() ?? ''}`;
                    return aName.localeCompare(bName);
                })
                .map((user) => ({
                    fullName: `${user?.firstName} ${user?.lastName}`,
                    id: user?.id as string,
                    groupedBy: user?.active ? 'Active' : 'Inactive',
                    disabled: risk?.owner
                        ? risk.owner.firstName?.toLowerCase() === user?.firstName?.toLowerCase() &&
                          risk.owner.lastName?.toLowerCase() === user?.lastName?.toLowerCase()
                            ? false
                            : !user?.active
                        : !user?.active,
                    active: !!user?.active,
                }));
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setOwners(studyMembers);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [ownerData?.studyMembers]);

    const { data: riskControlStrategyData, loading: loadingRiskControlStrategyData } = useQuery<getNamedItemsByType>(
        GET_NAMED_ITEMS_BY_TYPE,
        {
            variables: { type: NamedItemType.RISK_CONTROL_STRATEGY },
        },
    );

    const { data: riskCategoryData, loading: loadingRiskCategoryData } = useQuery<getNamedItemsByType>(
        GET_NAMED_ITEMS_BY_TYPE,
        {
            variables: { type: NamedItemType.RISK_CATEGORY },
        },
    );

    const { data: riskVendorData, loading: loadingRiskVendorData } = useQuery<getNamedItemsByType>(
        GET_NAMED_ITEMS_BY_TYPE,
        {
            variables: { type: NamedItemType.VENDOR },
        },
    );

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/purity
    const countries = Object.assign(
        [],
        getData().map((countryObject: { code: string; name: string }) => ({
            code: countryObject.code,
            name: countryObject.name,
        })),
    );

    const {
        control,
        handleSubmit,
        reset,
        getValues,
        setValue,
        resetField,
        clearErrors,
        formState: { isValid, isDirty, isSubmitting, dirtyFields },
        watch,
    } = useForm<RiskFormInputs>({
        mode: 'all',
        defaultValues: {
            reviewRequired: risk?.reviewRequired ?? true,
            internal: risk?.internalOnly ?? false,
            status: risk
                ? statuses.find((status) => status.text === risk.status)
                : (statuses.find((status) => status.id === 'DRAFT') ?? null),
            reviewFrequencyUnit: risk?.reviewFrequencyUnits
                ? reviewFrequencyUnits.find((unit) => unit.id === risk?.reviewFrequencyUnits)
                : null,
            reviewFrequencyPeriod: risk?.reviewFrequencyPeriod ?? null,
            dateIdentified: risk?.dateIdentified ? javascriptDateWithoutTimeFromUTC(risk.dateIdentified) : null,
            closedDate: risk?.closedDate ? javascriptDateWithoutTimeFromUTC(risk.closedDate) : null,
            responsibleFunction: risk?.responsibleFunction
                ? {
                      ...risk.responsibleFunction,
                      groupedBy: risk.responsibleFunction.active ? 'Active' : 'Inactive',
                      disabled: false,
                  }
                : null,
            owner: risk ? (owners?.find((ownerObject) => ownerObject?.id === risk?.ownerId) ?? null) : null,
            controlStrategy: risk?.controlStrategy
                ? {
                      ...risk.controlStrategy,
                      groupedBy: risk.controlStrategy.active ? 'Active' : 'Inactive',
                      disabled: false,
                  }
                : null,
            category: risk?.category
                ? {
                      ...risk.category,
                      groupedBy: risk.category.active ? 'Active' : 'Inactive',
                      disabled: false,
                  }
                : null,
            vendors:
                risk?.vendors?.map((riskVendor) => ({
                    id: riskVendor?.id,
                    name: riskVendor?.name,
                    groupedBy: riskVendor?.active ? 'Active' : 'Inactive',
                    disabled: false,
                    active: riskVendor?.active,
                    __typename: riskVendor?.__typename,
                })) ?? [],
            countries:
                risk?.countries?.filter(notEmpty).map((country) => ({
                    code: country?.code ?? '',
                    name: country?.name ?? '',
                })) ?? [],
            statement: risk?.statement ?? null,
            qualityIssue: risk?.qualityIssue
                ? {
                      ...risk.qualityIssue,
                      groupedBy: risk.qualityIssue.active ? 'Active' : 'Inactive',
                      disabled: false,
                  }
                : null,
            qualityIssueReference: risk?.qualityIssueReference ?? null,
        },
    });

    const status = watch('status');
    const responsibleFunction = watch('responsibleFunction');
    const owner = watch('owner');
    const controlStrategy = watch('controlStrategy');
    const reviewFrequencyPeriod = watch('reviewFrequencyPeriod');
    const reviewFrequencyUnit = watch('reviewFrequencyUnit');
    const reviewRequired = watch('reviewRequired');

    const notApplicableStatusSelected = status?.id === statuses.find((s) => s.id === RiskStatusType.NOT_APPLICABLE)?.id;
    const closedStatusSelected = status?.id === statuses.find((s) => s.id === RiskStatusType.CLOSED)?.id;
    const obsoleteStatusSelected = status?.id === statuses.find((s) => s.id === RiskStatusType.OBSOLETE)?.id;

    const createdBy = `${stringDateWithoutTimeFromUtc(risk?.created, 'DD-MMM-YYYY')} by ${risk?.createdBy}`;
    const updatedBy = `${stringDateWithoutTimeFromUtc(risk?.updated, 'DD-MMM-YYYY')} by ${risk?.updatedBy}`;

    useEffect(() => {
        if (risk && owners && owners.length > 0) {
            reset({
                ...getValues(),
                owner: owners.find((ownerObject) => ownerObject?.id === risk.ownerId) ?? null,
            });
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [owners]);

    const skipReviewRequiredResetRef = React.useRef(false);

    useEffect(() => {
        if (skipReviewRequiredResetRef.current) {
            skipReviewRequiredResetRef.current = false;
            return;
        }
        resetField('reviewFrequencyPeriod', {
            defaultValue: !reviewRequired ? 0 : (risk?.reviewFrequencyPeriod ?? null),
        });
        resetField('reviewFrequencyUnit', {
            defaultValue: !reviewRequired
                ? { id: 'NONE', text: 'None' }
                : (reviewFrequencyUnits.find((unit) => unit.id === risk?.reviewFrequencyUnits) ?? null),
        });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [reviewRequired]);

    const currentStatusRef = React.useRef(status);
    const previousStatusRef = React.useRef(status);
    const previousResponsibleFunctionRef = React.useRef(responsibleFunction);
    const previousOwnerRef = React.useRef(owner);
    const previousControlStrategyRef = React.useRef(controlStrategy);
    const previousReviewFrequencyPeriodRef = React.useRef(reviewFrequencyPeriod);
    const previousReviewFrequencyUnitRef = React.useRef(reviewFrequencyUnit);
    const previousReviewRequiredRef = React.useRef(reviewRequired);
    const closedDate = watch('closedDate');
    const previousClosedDateRef = React.useRef(closedDate);

    useEffect(() => {
        previousStatusRef.current = currentStatusRef.current;
        currentStatusRef.current = status;
        previousResponsibleFunctionRef.current = responsibleFunction;
        previousOwnerRef.current = owner;
        previousControlStrategyRef.current = controlStrategy;
        previousReviewFrequencyPeriodRef.current = reviewFrequencyPeriod;
        previousReviewFrequencyUnitRef.current = reviewFrequencyUnit;
        previousReviewRequiredRef.current = reviewRequired;
        previousClosedDateRef.current = closedDate;

        if (
            obsoleteStatusSelected &&
            previousStatusRef.current?.id !== statuses.find((s) => s.id === RiskStatusType.OBSOLETE)?.id
        ) {
            showObsoleteStatusDialog.setTrue();
        }

        if (notApplicableStatusSelected) {
            const hasResponsibleFunction = !!getValues().responsibleFunction;
            const hasOwner = !!getValues().owner;
            const hasControlStrategy = !!getValues().controlStrategy;
            const hasReviewFrequencyPeriod = !!getValues().reviewFrequencyPeriod;
            const hasReviewFrequencyUnit = !!reviewFrequencyUnits.find(
                (unit) => unit.id === getValues().reviewFrequencyUnit?.id,
            );
            const hasReviewRequired = !!getValues().reviewRequired;
            if (
                hasResponsibleFunction ||
                hasOwner ||
                hasControlStrategy ||
                hasReviewFrequencyPeriod ||
                hasReviewFrequencyUnit ||
                hasReviewRequired
            ) {
                setValue('responsibleFunction', null);
                setValue('owner', null);
                setValue('controlStrategy', null);
                setValue('reviewRequired', false);
                setValue('reviewFrequencyUnit', { id: 'NONE', text: 'None' });
                setValue('reviewFrequencyPeriod', 0);
                showApplyNotApplicableStatusDialog.setTrue();
            }
        }

        if (closedStatusSelected) {
            const statusChanged = previousStatusRef.current?.id !== status?.id;
            const wasReviewRequired = Boolean(getValues().reviewRequired);

            if (statusChanged) {
                setValue('reviewRequired', false);
                setValue('reviewFrequencyUnit', { id: 'NONE', text: 'None' });
                setValue('reviewFrequencyPeriod', 0);
                setValue('closedDate', getValues().closedDate ?? getUTCDate());

                if (wasReviewRequired) {
                    showApplyClosedStatusDialog.setTrue();
                }
            }
        } else if (previousStatusRef.current?.id === statuses.find((s) => s.id === RiskStatusType.CLOSED)?.id) {
            setValue('closedDate', null);
            clearErrors('closedDate');
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [status]);

    const dialogTitle = risk
        ? `${tRoot('common:edit')} ${t('title')}: ${risk.recordId}`
        : `${tRoot('common:add')} ${t('title')}`;
    const primaryBtnLabel = risk ? tRoot('common:update') : tRoot('common:add');

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isDirty]);

    const onCloseHandler = async () => {
        if (componentHasChanges(COMPONENT_NAME) && Object.keys(dirtyFields).length > 0) {
            const confirmed = await confirmDiscardChanges('add-risk-unsaved-dialog');
            if (!confirmed) {
                return;
            }
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        onClose();
    };

    const internalHeight = canViewInternal ? 0 : 24;

    return (
        <DraggableDialog
            className={cn(styles.kWindowContent)}
            dataTestId="add-risk-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmit)()}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            initialHeight={
                (risk
                    ? risk.associatedVariableIds && risk.associatedVariableIds.length > 0
                        ? window.innerHeight >= 800
                            ? 800
                            : 725
                        : 725
                    : 680) - internalHeight
            }
            initialWidth={542}
            createdUpdatedInfo={risk ? <CreatedUpdatedInfo created={createdBy} updated={updatedBy} /> : undefined}
            createdUpdatedBackgroundColour="gray"
            bodyPadding="none"
        >
            <div
                className={cn('k-w-full k-h-full k-d-flex-col k-pl-3.5 k-pr-3.5 k-pb-3.5', {
                    'k-pt-3.5': risk && risk.associatedVariableIds && risk.associatedVariableIds.length > 0,
                    'k-pt-1': !(risk && risk.associatedVariableIds && risk.associatedVariableIds.length > 0),
                })}
            >
                {risk && risk.associatedVariableIds && risk.associatedVariableIds.length > 0 ? (
                    <div className="k-pb-md">
                        <InfoField
                            type="Warning"
                            message={t('warnings.associatedCriticalToQualityFactors', {
                                count: risk.associatedVariableIds.length,
                            })}
                        />
                    </div>
                ) : null}
                <form id="add-risk" className="k-w-full k-h-full">
                    <div className={cn('k-mb-3 k-pt-1', ctqStyles.adornedElementContainer)}>
                        <div
                            className={cn(
                                'k-shrink-0 k-d-flex k-justify-content-left k-ml-2 k-mr-2',
                                styles.formFieldGap,
                            )}
                        >
                            <div className="k-d-flex k-gap-3">
                                <div className={cn(ctqStyles.statusSplitter)}>
                                    <div className="k-d-flex k-gap-3 k-align-items-center">
                                        <Controller
                                            control={control}
                                            name="reviewRequired"
                                            render={({ field: { onChange, value } }) => (
                                                <CoreCheckbox
                                                    data-testid="risk-field-review-required"
                                                    onChange={onChange}
                                                    value={value}
                                                    disabled={
                                                        notApplicableStatusSelected ||
                                                        closedStatusSelected ||
                                                        obsoleteStatusSelected
                                                    }
                                                    label={t('fields.reviewRequired')}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <span className={cn('k-label')}>{t('fields.reviewFrequency')}</span>
                                    <div className="k-d-flex k-gap-2">
                                        <div className={cn('!k-basis-1/2', ctqStyles.minWidthFix)}>
                                            <NumericTextBoxController
                                                control={control}
                                                name="reviewFrequencyPeriod"
                                                data-testid="risk-field-review-frequency-period"
                                                required={!!reviewRequired}
                                                readOnly={!reviewRequired}
                                                validityStyles={!!reviewRequired}
                                                className={cn(ctqStyles.reviewFrequencyPeriod)}
                                                min={reviewRequired ? RiskService.reviewFrequencyNumberMin : undefined}
                                                max={reviewRequired ? RiskService.reviewFrequencyNumberMax : undefined}
                                                format="0"
                                            />
                                        </div>
                                        <div
                                            className={cn('!k-basis-1/2', ctqStyles.minWidthFix, ctqStyles.dropdownFix)}
                                        >
                                            <BasicDropDownController
                                                control={control}
                                                name="reviewFrequencyUnit"
                                                data-testid="risk-field-review-frequency-unit"
                                                textField="text"
                                                dataItemKey="id"
                                                filterable
                                                allowClear
                                                data={reviewFrequencyUnits ?? []}
                                                required={!!reviewRequired}
                                                readOnly={!reviewRequired}
                                                validityStyles={!!reviewRequired}
                                                className={cn(ctqStyles.reviewFrequencyUnits)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {canViewInternal && (
                        <div className="k-shrink-0 k-d-flex k-pb-sm">
                            <Controller
                                control={control}
                                name="internal"
                                render={({ field: { onChange, value } }) => (
                                    <CoreCheckbox
                                        data-testid="risk-field-internal"
                                        onChange={onChange}
                                        value={value}
                                        label={t('fields.internal')}
                                    />
                                )}
                            />
                        </div>
                    )}
                    <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-sm', styles.formFieldGap)}>
                        <div className={cn('!k-basis-1/3', ctqStyles.minWidthFix)}>
                            <DatePickerController
                                control={control}
                                name="dateIdentified"
                                label={t('fields.dateIdentified')}
                                data-testid="risk-field-date-identified"
                                fillMode="solid"
                            />
                        </div>
                        <div className={cn('!k-basis-2/3', ctqStyles.minWidthFix, ctqStyles.dropdownFix)}>
                            <BasicDropDownController
                                control={control}
                                name="responsibleFunction"
                                data-testid="risk-field-responsible-function"
                                data={responsibleFunctions}
                                disabled={loadingResponsibleFunctionData}
                                loading={loadingResponsibleFunctionData}
                                readOnly={notApplicableStatusSelected}
                                textField="name"
                                groupField="groupedBy"
                                activeField="active"
                                label={t('fields.responsibleFunction')}
                                dataItemKey="id"
                                filterable
                                allowClear
                            />
                        </div>
                    </div>
                    <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-sm">
                        <TextAreaController
                            className={styles.textAreaOverrides}
                            maxLength={600}
                            control={control}
                            name="statement"
                            data-testid="risk-field-statement"
                            label={t('fields.statement')}
                            placeholder={t('statementGuidelinePlaceholder')}
                            hintText={`${t('statementGuidelineInstruction')}\n${t('statementGuidelineSample')}`}
                            hintTextTooltipPosition="top"
                            hintTextMaxWidth={300}
                            required
                        />
                    </div>
                    <div className={cn('k-mb-3', ctqStyles.adornedElementContainer)}>
                        <div
                            className={cn('k-shrink-0 k-d-flex k-justify-content-center', styles.formFieldGap)}
                            style={{ marginLeft: '6px', marginRight: '6px' }}
                        >
                            <div className={cn('!k-basis-1/2', ctqStyles.minWidthFix, ctqStyles.dropdownFix)}>
                                <BasicDropDownController
                                    control={control}
                                    name="qualityIssue"
                                    label={t('fields.qualityIssue')}
                                    data-testid="risk-field-quality-issue"
                                    disabled={loadingQualityIssueData}
                                    loading={loadingQualityIssueData}
                                    textField="name"
                                    dataItemKey="id"
                                    groupField="groupedBy"
                                    activeField="active"
                                    filterable
                                    allowClear
                                    data={
                                        qualityIssueData?.namedItemsByType
                                            ? mapNamedItems(
                                                  qualityIssueData.namedItemsByType,
                                                  risk?.qualityIssue?.name ? [risk.qualityIssue.name] : undefined,
                                              )
                                            : []
                                    }
                                />
                            </div>
                            <div className={cn('!k-basis-1/2', ctqStyles.minWidthFix, ctqStyles.dropdownFix)}>
                                <TextBoxController
                                    maxLength={70}
                                    control={control}
                                    name="qualityIssueReference"
                                    data-testid="risk-field-quality-issue-reference"
                                    label={t('fields.qualityIssueReference')}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-sm', styles.formFieldGap)}>
                        <div className={cn('!k-basis-1/2', ctqStyles.minWidthFix, ctqStyles.dropdownFix)}>
                            <BasicDropDownController
                                control={control}
                                name="status"
                                label={t('fields.status')}
                                data-testid="risk-field-status"
                                textField="text"
                                dataItemKey="id"
                                filterable
                                allowClear={false}
                                required
                                data={
                                    risk && risk.status !== statuses.find((s) => s.id === RiskStatusType.DRAFT)?.text
                                        ? statuses?.filter((s) => s.id !== 'DRAFT')
                                        : (statuses ?? [])
                                }
                            />
                        </div>
                        <div className={cn('!k-basis-1/2', ctqStyles.minWidthFix)}>
                            <DatePickerController
                                control={control}
                                name="closedDate"
                                label={t('fields.closedDate')}
                                data-testid="risk-field-closed-date"
                                fillMode="solid"
                                required={closedStatusSelected}
                                readonly={!closedStatusSelected}
                            />
                        </div>
                    </div>
                    <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-sm', styles.formFieldGap)}>
                        <div className={cn('k-w-full', ctqStyles.minWidthFix, ctqStyles.dropdownFix)}>
                            <BasicDropDownController
                                control={control}
                                name="controlStrategy"
                                data-testid="risk-field-control-strategy"
                                data={
                                    riskControlStrategyData?.namedItemsByType
                                        ? mapNamedItems(
                                              riskControlStrategyData.namedItemsByType,
                                              risk?.controlStrategy?.name ? [risk.controlStrategy.name] : undefined,
                                          )
                                        : []
                                }
                                disabled={loadingRiskControlStrategyData}
                                loading={loadingRiskControlStrategyData}
                                readOnly={notApplicableStatusSelected}
                                textField="name"
                                groupField="groupedBy"
                                activeField="active"
                                label={t('fields.controlStrategy')}
                                dataItemKey="id"
                                filterable
                                allowClear
                            />
                        </div>
                    </div>
                    <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-sm', styles.formFieldGap)}>
                        <div className={cn('k-w-full', ctqStyles.minWidthFix, ctqStyles.dropdownFix)}>
                            <BasicDropDownController
                                control={control}
                                name="category"
                                data-testid="risk-field-category"
                                data={
                                    riskCategoryData?.namedItemsByType
                                        ? mapNamedItems(
                                              riskCategoryData.namedItemsByType,
                                              risk?.category?.name ? [risk.category.name] : undefined,
                                          )
                                        : []
                                }
                                disabled={loadingRiskCategoryData}
                                loading={loadingRiskCategoryData}
                                textField="name"
                                groupField="groupedBy"
                                activeField="active"
                                label={t('fields.category')}
                                dataItemKey="id"
                                filterable
                                allowClear
                            />
                        </div>
                    </div>
                    <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-sm', styles.formFieldGap)}>
                        <div className={cn('k-w-full', ctqStyles.minWidthFix, ctqStyles.dropdownFix)}>
                            <BasicDropDownController
                                control={control}
                                name="owner"
                                data-testid="risk-field-owner"
                                data={owners ?? []}
                                readOnly={notApplicableStatusSelected}
                                loading={loadingOwners}
                                textField="fullName"
                                groupField="groupedBy"
                                activeField="active"
                                label={t('fields.owner')}
                                dataItemKey="id"
                                filterable
                                allowClear
                            />
                        </div>
                    </div>
                    <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-sm">
                        <MultiSelectController
                            isEditing
                            control={control}
                            name="vendors"
                            data={
                                Array.isArray(riskVendorData?.namedItemsByType)
                                    ? mapNamedItems(
                                          riskVendorData.namedItemsByType,
                                          risk?.vendors
                                              ? (risk?.vendors?.map((v) => v?.name ?? undefined) as string[])
                                              : undefined,
                                      )
                                    : []
                            }
                            loading={loadingRiskVendorData}
                            disabled={loadingRiskVendorData}
                            data-testid="risk-field-vendors"
                            label={t('fields.vendors')}
                            textField="name"
                            dataItemKey="id"
                            groupField="groupedBy"
                            activeField="active"
                            filterable
                            allowCustom={false}
                        />
                    </div>
                    <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-sm">
                        <MultiSelectController
                            isEditing
                            control={control}
                            name="countries"
                            data={mapCountries(countries, (risk?.countries ?? []) as Country[])}
                            data-testid="risk-field-countries"
                            label={t('fields.countries')}
                            textField="name"
                            dataItemKey="code"
                            filterable
                            allowCustom={false}
                            isCountryMultiSelect
                        />
                    </div>
                </form>
            </div>

            {showApplyNotApplicableStatusDialog.value && (
                <ConfirmationDialog
                    dataTestId="confirm-apply-not-applicable-status-dialog"
                    title={t('warnings.notApplicableRiskStatus.title')}
                    onSubmit={() => {
                        resetField('responsibleFunction', {
                            defaultValue: risk?.responsibleFunction
                                ? {
                                      ...risk.responsibleFunction,
                                      groupedBy: risk.responsibleFunction.active ? 'Active' : 'Inactive',
                                      disabled: !risk.responsibleFunction.active,
                                  }
                                : null,
                        });
                        resetField('owner', {
                            defaultValue: risk
                                ? (owners?.find((ownerObject) => ownerObject?.id === risk?.ownerId) ?? null)
                                : null,
                        });
                        resetField('controlStrategy', {
                            defaultValue: risk?.controlStrategy
                                ? {
                                      ...risk.controlStrategy,
                                      groupedBy: risk.controlStrategy.active ? 'Active' : 'Inactive',
                                      disabled: !risk.controlStrategy.active,
                                  }
                                : null,
                        });
                        resetField('reviewFrequencyPeriod', {
                            defaultValue: risk?.reviewFrequencyPeriod ?? null,
                        });
                        resetField('reviewFrequencyUnit', {
                            defaultValue: risk?.reviewFrequencyUnits
                                ? reviewFrequencyUnits.find((unit) => unit.id === risk?.reviewFrequencyUnits)
                                : null,
                        });
                        resetField('reviewRequired', {
                            defaultValue: risk?.reviewRequired ?? true,
                        });
                        setValue('responsibleFunction', null);
                        setValue('owner', null);
                        setValue('controlStrategy', null);
                        setValue('reviewFrequencyPeriod', 0);
                        setValue('reviewFrequencyUnit', { id: 'NONE', text: 'None' });
                        setValue('reviewRequired', false);
                        showApplyNotApplicableStatusDialog.setFalse();
                    }}
                    onClose={() => {
                        // Reset to previous values
                        setValue('status', previousStatusRef.current);
                        setValue('responsibleFunction', previousResponsibleFunctionRef.current);
                        setValue('owner', previousOwnerRef.current);
                        setValue('controlStrategy', previousControlStrategyRef.current);
                        setValue('reviewFrequencyPeriod', previousReviewFrequencyPeriodRef.current);
                        setValue('reviewFrequencyUnit', previousReviewFrequencyUnitRef.current);
                        setValue('reviewRequired', previousReviewRequiredRef.current);
                        skipReviewRequiredResetRef.current = true;
                        showApplyNotApplicableStatusDialog.setFalse();
                    }}
                    primaryBtnLabel={tRoot('common:yes')}
                    secondaryBtnLabel={tRoot('common:no')}
                    primaryBtnDisabled={false}
                    ignoreReadonlyCtx
                    width={550}
                >
                    {t('warnings.notApplicableRiskStatus.body')}
                </ConfirmationDialog>
            )}

            {showApplyClosedStatusDialog.value && (
                <ConfirmationDialog
                    dataTestId="confirm-apply-closed-status-dialog"
                    title={t('warnings.closedRiskStatus.title')}
                    onSubmit={() => {
                        showApplyClosedStatusDialog.setFalse();
                    }}
                    onClose={() => {
                        setValue('status', previousStatusRef.current);
                        setValue('reviewFrequencyPeriod', previousReviewFrequencyPeriodRef.current);
                        setValue('reviewFrequencyUnit', previousReviewFrequencyUnitRef.current);
                        setValue('reviewRequired', previousReviewRequiredRef.current);
                        setValue('closedDate', previousClosedDateRef.current);
                        skipReviewRequiredResetRef.current = true;
                        showApplyClosedStatusDialog.setFalse();
                    }}
                    primaryBtnLabel={tRoot('common:yes')}
                    secondaryBtnLabel={tRoot('common:no')}
                    primaryBtnDisabled={false}
                    ignoreReadonlyCtx
                    width={650}
                >
                    {t('warnings.closedRiskStatus.body')}
                </ConfirmationDialog>
            )}
            {showObsoleteStatusDialog.value && (
                <ConfirmationDialog
                    dataTestId="draft-status-dialog"
                    title={t('warnings.obsoleteStatus.title')}
                    onSubmit={() => {
                        setValue('reviewRequired', false);
                        setValue('reviewFrequencyUnit', { id: 'NONE', text: 'None' });
                        setValue('reviewFrequencyPeriod', 0);
                        showObsoleteStatusDialog.setFalse();
                    }}
                    onClose={() => {
                        setValue('status', previousStatusRef.current);
                        showObsoleteStatusDialog.setFalse();
                    }}
                    primaryBtnLabel={tRoot('common:yes')}
                    secondaryBtnLabel={tRoot('common:no')}
                    primaryBtnDisabled={false}
                    ignoreReadonlyCtx
                    width={650}
                >
                    {t('warnings.obsoleteStatus.body')}
                </ConfirmationDialog>
            )}
        </DraggableDialog>
    );
};

export default AddRiskDialog;
